import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Form, Formik, Field } from "formik";
import { Autocomplete, TextField, Stack, Tooltip, Alert } from "@mui/material";
import AlertPopup from "../../../../../../../Components/Bits/AlertPopup";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { fetchTeamMembers } from "../../Teams/Services/api";
import withAuth from "../../../../../../../Hoc/withAuth";
import { useParams, useNavigate } from "react-router-dom";

import * as Yup from "yup";

import GroupAddIcon from "@mui/icons-material/GroupAdd";

import SelectLanguagesComp from "../../../../../../../Components/FormComponents/SelectLanguagesComp";
import TextfieldWrapper from "../../../../../../../Components/FormComponents/TextfieldWrapper";
import SelectWrapper from "../../../../../../../Components/FormComponents/Select";

const CreateEvaluations = ({ user, getAllEvaluations }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  // QA Evaluation validation schema for all fields
  const validationSchema = Yup.object().shape({
    call_center_agent: Yup.object()
      .nullable()
      .required("Call Center Agent is required"),
    unique_id: Yup.string().required("Unique ID is required"),
    disposition: Yup.string().required("Disposition is required"),
    team_member_id: Yup.object().nullable().required("Team Member is required"),
    team_member_type: Yup.string(),
    evaluation_type: Yup.string().required("Evaluation Type is required"),
    language: Yup.array().min(1, "Language is required"),

    Link: Yup.string().when("evaluation_type", {
      is: (evaluation_type) => evaluation_type === "Ticket",
      then: Yup.string().required("Link is required"),
    }),
  });

  const { data: teamMembers = [], isSuccess: teamMembersLoaded } = useQuery(
    ["teamMembers", id],
    () =>
      fetchTeamMembers(id).then((res) => {
        return res.data.data;
      }),
    { enabled: !!id },
  );

  const redirectToTeams = () => {
    navigate(`/CDA/inbound/admin/${id}/Teams`);
    handleClose();
  };

  const Teammembers = teamMembers;

  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  };

  const allUsers = useQuery({
    queryKey: ["all Users"],
    queryFn: () =>
      axios.get(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/teammembers/inboundAllUsers`,
        config,
      ),
  });

  const users = allUsers?.data?.data.data;

  const createEvaluation = useMutation({
    mutationFn: async (data) =>
      await axios.post(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/Evaluations/${id}/evaluations`,
        data,
        config,
      ),
    onSuccess: () => {
      getAllEvaluations.refetch();
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <React.Fragment>
      <Button variant="contained" color="primary" onClick={handleClickOpen}>
        Create Evaluation
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Create QA Evaluation
        </DialogTitle>
        <DialogContent>
          {teamMembersLoaded && teamMembers.length === 0 && (
            <Tooltip title="Add Team Members">
              <Button
                variant="contained"
                color="primary"
                startIcon={<GroupAddIcon />}
                onClick={redirectToTeams}
                size="normal"
              >
                Add Team Members
              </Button>
            </Tooltip>
          )}
          {teamMembersLoaded && teamMembers.length === 0 && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
              No team members are available. You need to add team members before
              creating an evaluation.
            </Alert>
          )}

          <Formik
            validationSchema={validationSchema}
            initialValues={{
              call_center_agent: null,
              unique_id: "",
              disposition: "",
              team_member_id: null,
              team_member_type: "",
              evaluation_type: "",
              status: "New",
              campaign_id: id,
              language: [],
              Link: "",
            }}
            onSubmit={(values) => {
              const updatedValues = {
                ...values,
                call_center_agent: values.call_center_agent
                  ? values.call_center_agent.id
                  : null,
                team_member_id: values.team_member_id
                  ? values.team_member_id.user_id
                  : null,
              };

              createEvaluation.mutate(updatedValues);

              handleClose();
            }}
          >
            {({ setFieldValue, values, errors, touched, handleBlur }) => (
              <Form>
                <Stack sx={{ py: 2 }} spacing={2}>
                  <Field name="call_center_agent">
                    {({ field }) => (
                      <Autocomplete
                        {...field}
                        options={users || []}
                        getOptionLabel={(option) =>
                          option
                            ? `${option.name} - ${option.surname} - ${option.email}`
                            : ""
                        }
                        onChange={(event, newValue) =>
                          setFieldValue("call_center_agent", newValue)
                        }
                        onBlur={handleBlur}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Call Center Agent"
                            fullWidth
                            margin="normal"
                            error={
                              touched.call_center_agent &&
                              Boolean(errors.call_center_agent)
                            }
                            helperText={
                              touched.call_center_agent &&
                              errors.call_center_agent
                            }
                          />
                        )}
                      />
                    )}
                  </Field>

                  <TextfieldWrapper name="unique_id" label="Unique ID" />
                  <TextfieldWrapper name="disposition" label="Disposition" />

                  <Field name="team_member_id">
                    {({ field }) => (
                      <Autocomplete
                        {...field}
                        options={Teammembers || []}
                        getOptionLabel={(option) =>
                          option ? `${option.user_type} - ${option.email}` : ""
                        }
                        onChange={(event, newValue) => {
                          setFieldValue("team_member_id", newValue);
                          if (newValue) {
                            setFieldValue(
                              "team_member_type",
                              newValue.user_type,
                            );
                          } else {
                            setFieldValue("team_member_type", "");
                          }
                        }}
                        onBlur={handleBlur}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Team Member"
                            fullWidth
                            error={
                              touched.team_member_id &&
                              Boolean(errors.team_member_id)
                            }
                            helperText={
                              touched.team_member_id && errors.team_member_id
                            }
                          />
                        )}
                        disabled={teamMembers.length === 0}
                      />
                    )}
                  </Field>

                  <SelectWrapper
                    name="evaluation_type"
                    label="Evaluation Type"
                    options={[
                      { value: "sale", label: "Sale Verification" },
                      { value: "Inbound Call", label: "Inbound Call" },
                      { value: "Outbound Call", label: "Outbound Call" },
                      { value: "Ticket", label: "Ticket" },
                    ]}
                  />

                  {values?.evaluation_type === "Ticket" && (
                    <>
                      <TextfieldWrapper name="Link" label="Link" />
                    </>
                  )}

                  <SelectLanguagesComp
                    name="language"
                    label="Select Languages"
                  />

                  <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      type="submit"
                      disabled={teamMembers.length === 0}
                    >
                      Submit
                    </Button>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
      <AlertPopup
        open={createEvaluation.isError}
        severity="error"
        message="Error creating Evaluation"
      />
      <AlertPopup
        open={createEvaluation.isSuccess}
        severity="success"
        message="Evaluation created successfully"
      />
    </React.Fragment>
  );
};

export default withAuth(CreateEvaluations);
